import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Loading from 'react-loading';
import DayPickerInput from 'react-day-picker/DayPickerInput';
import { Form } from 'semantic-ui-react';
import DialogTitle from '@material-ui/core/DialogTitle';
import Dialog from '@material-ui/core/Dialog';
import { useMutation } from '@apollo/react-hooks';
import moment from 'moment';
import { MdSend } from 'react-icons/md';
import { toast } from 'react-toastify';

import { Main, Button, Send } from './styles';

import {
  ADD_PLATES_BLACKLIST,
  UPDATE_PLATES_BLACKLIST
} from '../../../services/graphql/mutations';

import { schema, formInitialState, formErrorInitialState } from './constants';

export default function BlacklistModal({
  edit,
  onClose,
  open,
  malls,
  motives,
  selected = {}
}) {
  const [
    {
      license_plate,
      mall,
      country_id,
      motive,
      description,
      date_ini,
      date_end
    },
    setFormState
  ] = useState(formInitialState);
  const [
    {
      license_plateError,
      mallError,
      motiveError,
      descriptionError,
      date_endError,
      date_iniError
    },
    setFormErrorState
  ] = useState(formErrorInitialState);

  const [addBlacklist, { loading: addLoading }] = useMutation(
    ADD_PLATES_BLACKLIST
  );
  const [updateBlacklist, { loading: updateLoading }] = useMutation(
    UPDATE_PLATES_BLACKLIST
  );

  useEffect(() => {
    if (open) {
      setFormState(formInitialState);
      setFormErrorState(formErrorInitialState);
    }

    if (edit && open) {
      const {
        country_id,
        date_end,
        date_ini,
        description,
        license_plate,
        motives_id: motive,
        mall_id: mall
      } = selected;

      setFormState({
        license_plate,
        mall,
        country_id,
        motive,
        description,
        date_ini,
        date_end
      });
    }
  }, [open, edit, selected]);

  function handleOnChange(name, value) {
    setFormState(prevState => ({ ...prevState, [name]: value }));
  }

  async function handleSubmit() {
    try {
      setFormErrorState(formErrorInitialState);

      await schema.validate(
        {
          description,
          motive,
          country_id,
          mall,
          license_plate,
          date_ini,
          date_end
        },
        { abortEarly: false }
      );

      if (edit) {
        const editBlacklistInput = {
          id: selected.id,
          country_id,
          license_plate,
          mall_id: mall,
          motives_id: motive,
          description,
          date_ini: moment(date_ini)
            .format('YYYY-MM-DD')
            .toString(),
          date_end: moment(date_end)
            .format('YYYY-MM-DD')
            .toString()
        };

        await updateBlacklist({
          variables: {
            editBlacklistInput
          }
        });
      } else {
        const blacklistInput = {
          country_id,
          description,
          license_plate,
          active: true,
          mall_id: mall,
          motives_id: motive,
          date_ini: moment(date_ini)
            .format('YYYY-MM-DD')
            .toString(),
          date_end: moment(date_end)
            .format('YYYY-MM-DD')
            .toString()
        };

        await addBlacklist({
          variables: {
            blacklistInput
          }
        });
      }

      setFormState(formInitialState);

      let successMessage;

      if (edit) successMessage = 'El registro fue editado exitosamente';
      else successMessage = 'El registro fue creado exitosamente';

      toast.success(successMessage);

      onClose();
    } catch (err) {
      if (err.inner) {
        err.inner.map(error =>
          setFormErrorState(prevState => ({
            ...prevState,
            [`${error.path}Error`]: true
          }))
        );
      } else {
        let errorMessage;

        if (edit) errorMessage = 'Error al intentar actualizar el registro';
        else errorMessage = 'Error al intentar crear el registro';

        toast.error(errorMessage);
      }
    }
  }

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      onClose={onClose}
      aria-labelledby="simple-dialog-title"
      open={open}
    >
      <DialogTitle id="simple-dialog-title">
        {edit ? 'Editar' : 'Ingresar'} Lista Negra
      </DialogTitle>
      <Main>
        <Form>
          <Form.Group>
            <Form.Input
              error={license_plateError}
              label="Patente *"
              placeholder="Patente"
              width={4}
              value={license_plate}
              onChange={(e, { value }) =>
                handleOnChange('license_plate', value)
              }
              className="plateInput"
            />
            <Form.Select
              error={mallError}
              label="Centro Comercial *"
              placeholder="Centro Comercial"
              options={malls}
              width={4}
              value={mall}
              onChange={(e, { value, options }) => {
                handleOnChange('mall', value);
                handleOnChange(
                  'country_id',
                  options.find(mall => mall.value === value).country_id
                );
              }}
            />
            <Form.Select
              error={motiveError}
              label="Motivos *"
              placeholder="Motivos"
              options={motives}
              width={4}
              value={motive}
              onChange={(e, { value }) => handleOnChange('motive', value)}
            />
          </Form.Group>

          <Form.Group>
            <Form.Field
              error={date_iniError}
              label="Fecha de Inicio *"
              control={DayPickerInput}
              value={date_ini}
              format="DD/MM/YYYY"
              placeholder="DD/MM/YYYY"
              onDayChange={day => handleOnChange('date_ini', day)}
            ></Form.Field>
            <Form.Field
              error={date_endError}
              label="Fecha de Termino *"
              control={DayPickerInput}
              value={date_end}
              format="DD/MM/YYYY"
              placeholder="DD/MM/YYYY"
              onDayChange={day => handleOnChange('date_end', day)}
            ></Form.Field>
          </Form.Group>

          <Form.Group>
            <Form.TextArea
              error={descriptionError}
              label="Descripción *"
              placeholder="Descripción"
              width={16}
              value={description}
              onChange={(e, { value }) => handleOnChange('description', value)}
              className="descriptionInput"
            />
          </Form.Group>
        </Form>
        <Button onClick={handleSubmit}>
          {addLoading || updateLoading ? (
            <Loading height={15} width={15} type="spin" />
          ) : (
            <Send>
              <MdSend />
              Enviar
            </Send>
          )}
        </Button>
      </Main>
    </Dialog>
  );
}

BlacklistModal.propTypes = {
  edit: PropTypes.bool,
  onClose: PropTypes.func,
  open: PropTypes.bool,
  selected: PropTypes.object,
  malls: PropTypes.array,
  motives: PropTypes.array
};
